/**
 * Set of string values that represent "no real data" in database columns.
 * Matched case-insensitively.
 */
const PLACEHOLDER_VALUES = new Set(["none", "null", "nan", "undefined", "n/a", "na", "-"]);

/**
 * Legacy marker used by an earlier preview build for review rejections. New
 * rejections persist SQL NULL so non-frontend readers never see bookkeeping as
 * an attribute value; this decoder remains only for preview rows already saved.
 */
export const EXPLICITLY_CLEARED_USER_VALUE = "\u001efranklin:explicit-clear:v1\u001e";

/** True only for the legacy reviewed-and-cleared marker. */
export function isExplicitlyClearedUserValue(value: string | null): boolean {
  return value === EXPLICITLY_CLEARED_USER_VALUE;
}

/**
 * A rejected machine finding is stored as SQL NULL. The legacy marker remains
 * readable for rows written by earlier preview builds.
 */
export function isRejectedUserValue(value: string | null): boolean {
  return value === null || isExplicitlyClearedUserValue(value);
}

/**
 * Returns true if a string value is semantically empty or a placeholder token
 * (e.g. "none", "null", "NaN", "N/A"). Case-insensitive.
 */
export function isPlaceholderValue(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  return PLACEHOLDER_VALUES.has(trimmed.toLowerCase());
}

/**
 * Returns the trimmed string if it holds a real value, or empty string
 * if it is null/undefined/empty/a placeholder token.
 */
export function sanitizeFieldValue(value: string | null | undefined): string {
  if (isPlaceholderValue(value)) return "";
  return value!.trim();
}
